import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { EventClickArg, DateSelectArg, EventContentArg } from '@fullcalendar/core';
import arLocale from '@fullcalendar/core/locales/ar';
import frLocale from '@fullcalendar/core/locales/fr';
import { lessonService, LessonResponse, LessonRequest, DayOfWeek, LessonValidationResponse } from '../../services/lessonService';
import { classeService, Classe } from '../../services/classeService';
import { teacherService, Teacher } from '../../services/teacherService';
import { courseService, Course } from '../../services/courseService';
import { studentService, Student } from '../../services/studentService';
import { absenceService } from '../../services/absenceService';
import { useModal } from '../../context/ModalContext';
import './Timetable.css';

const DAYS_OF_WEEK: DayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const DAY_OFFSETS: Record<DayOfWeek, number> = {
  MONDAY: 0,
  TUESDAY: 1,
  WEDNESDAY: 2,
  THURSDAY: 3,
  FRIDAY: 4,
  SATURDAY: 5,
  SUNDAY: 6,
};

const getCurrentWeekStart = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  // Get Monday of current week (Sunday = 0, so we adjust)
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

interface CalendarEvent {
  id: string;
  title?: string;
  start: Date;
  end: Date;
  extendedProps: LessonResponse & { durationText?: string };
  backgroundColor?: string;
  borderColor?: string;
  classNames?: string[];
}

const Timetable: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { showAlert, showConfirm } = useModal();

  const [lessons, setLessons] = useState<LessonResponse[]>([]);
  const [classes, setClasses] = useState<Classe[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState<LessonResponse | null>(null);
  const [editingLessonDate, setEditingLessonDate] = useState<Date | null>(null);
  const [formData, setFormData] = useState<LessonRequest>({
    classeId: '',
    teacherId: '',
    courseId: '',
    dayOfWeek: 'MONDAY',
    startTime: '08:00',
    endTime: '09:00',
    room: ''
  });

  // Validation state
  const [validation, setValidation] = useState<LessonValidationResponse | null>(null);
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Teacher lock state
  const [isTeacherLocked, setIsTeacherLocked] = useState(true);

  // Absence/Attendance state
  const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
  const [classStudents, setClassStudents] = useState<Student[]>([]);
  const [absentStudentIds, setAbsentStudentIds] = useState<string[]>([]);
  const [currentLessonForAttendance, setCurrentLessonForAttendance] = useState<LessonResponse | null>(null);
  const [currentAttendanceDate, setCurrentAttendanceDate] = useState<Date | null>(null);

  // Get locale for FullCalendar
  const calendarLocale = useMemo(() => {
    switch (i18n.language) {
      case 'ar': return arLocale;
      case 'fr': return frLocale;
      default: return undefined; // English is default
    }
  }, [i18n.language]);

  // Get direction for RTL support
  const calendarDirection = i18n.language === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedClassId) {
      loadLessons();
    }
  }, [selectedClassId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [classesData, teachersData, coursesData] = await Promise.all([
        classeService.getAllClasses(),
        teacherService.getAllTeachers(),
        courseService.getAllCourses()
      ]);
      setClasses(classesData);
      setTeachers(teachersData);
      setCourses(coursesData);

      // Auto-select first class if available
      if (classesData.length > 0) {
        setSelectedClassId(classesData[0].id!);
      }
    } catch (err: any) {
      setError(t('timetable.failedToLoad'));
      console.error('Failed to load initial data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLessons = async () => {
    if (!selectedClassId) return;

    try {
      setLoading(true);
      const data = await lessonService.getLessonsByClass(selectedClassId);
      setLessons(data);
      setError('');
    } catch (err: any) {
      setError(t('timetable.failedToLoad'));
      console.error('Failed to load lessons:', err);
    } finally {
      setLoading(false);
    }
  };

  // Generate consistent color for a course based on its ID
  const getCourseColor = (courseId: string): string => {
    let hash = 0;
    for (let i = 0; i < courseId.length; i++) {
      hash = courseId.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    const hue = Math.abs(hash) % 360;
    const saturation = 45 + (Math.abs(hash >> 8) % 15);
    const lightness = 65 + (Math.abs(hash >> 16) % 10);
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  // Find the next upcoming lesson
  const getNextLesson = (): string | null => {
    if (lessons.length === 0) return null;

    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const dayMap: { [key in DayOfWeek]: number } = {
      SUNDAY: 0,
      MONDAY: 1,
      TUESDAY: 2,
      WEDNESDAY: 3,
      THURSDAY: 4,
      FRIDAY: 5,
      SATURDAY: 6
    };

    let nextLessonId: string | null = null;
    let minDiff = Infinity;

    lessons.forEach((lesson: LessonResponse) => {
      const lessonDay = dayMap[lesson.dayOfWeek];
      const [lessonHour, lessonMin] = lesson.startTime.split(':').map(Number);
      const lessonTime = lessonHour * 60 + lessonMin;

      let dayDiff = lessonDay - currentDay;
      if (dayDiff < 0) dayDiff += 7;

      let totalDiff = dayDiff * 24 * 60 + (lessonTime - currentTime);

      if (totalDiff > 0 && totalDiff < minDiff) {
        minDiff = totalDiff;
        nextLessonId = lesson.id;
      }
    });

    return nextLessonId;
  };

  const nextLessonId = useMemo(() => getNextLesson(), [lessons]);

  // Convert lessons to FullCalendar events
  const events: CalendarEvent[] = useMemo(() => {
    const weekStart = getCurrentWeekStart();
    return lessons.map(lesson => {
      const lessonDate = new Date(weekStart);
      lessonDate.setDate(weekStart.getDate() + DAY_OFFSETS[lesson.dayOfWeek]);

      const [startHour, startMin] = lesson.startTime.split(':').map(Number);
      const [endHour, endMin] = lesson.endTime.split(':').map(Number);

      const start = new Date(lessonDate);
      start.setHours(startHour, startMin, 0, 0);

      const end = new Date(lessonDate);
      end.setHours(endHour, endMin, 0, 0);

      const durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      const durationText = hours > 0
        ? `${hours}h${minutes > 0 ? minutes.toString().padStart(2, '0') : ''}`
        : `${minutes}min`;

      const isNextLesson = lesson.id === nextLessonId;
      const courseColor = getCourseColor(lesson.courseId);

      return {
        id: lesson.id,
        title: lesson.courseName,
        start,
        end,
        backgroundColor: courseColor,
        borderColor: isNextLesson ? '#ff8c00' : 'transparent',
        classNames: isNextLesson ? ['next-lesson'] : [],
        extendedProps: { ...lesson, durationText }
      };
    });
  }, [lessons, nextLessonId]);

  const handleDateSelect = (selectInfo: DateSelectArg) => {
    const start = selectInfo.start;
    const dayMap: DayOfWeek[] = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    const dayOfWeek = dayMap[start.getDay()];

    const hours = String(start.getHours()).padStart(2, '0');
    const minutes = String(start.getMinutes()).padStart(2, '0');
    const startTime = `${hours}:${minutes}`;

    const endDate = new Date(start);
    endDate.setHours(endDate.getHours() + 1);
    const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;

    openCreateModal(dayOfWeek, startTime, endTime);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const lesson = clickInfo.event.extendedProps as LessonResponse & { durationText?: string };
    openEditModal(lesson, clickInfo.event.start || new Date());
  };

  const openCreateModal = (day?: DayOfWeek, startTime?: string, endTime?: string) => {
    setEditingLesson(null);
    setFormData({
      classeId: selectedClassId,
      teacherId: '',
      courseId: '',
      dayOfWeek: day || 'MONDAY',
      startTime: startTime || '08:00',
      endTime: endTime || '09:00',
      room: ''
    });
    setValidation(null);
    setIsTeacherLocked(true);
    setIsModalOpen(true);
  };

  const openEditModal = (lesson: LessonResponse, date?: Date) => {
    setEditingLesson(lesson);
    setEditingLessonDate(date || null);
    setFormData({
      classeId: lesson.classeId,
      teacherId: lesson.teacherId,
      courseId: lesson.courseId,
      dayOfWeek: lesson.dayOfWeek,
      startTime: lesson.startTime,
      endTime: lesson.endTime,
      room: lesson.room || ''
    });
    setValidation(null);

    const course = courses.find(c => c.id === lesson.courseId);
    setIsTeacherLocked(course?.teacherId === lesson.teacherId);

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingLesson(null);
    setEditingLessonDate(null);
    setValidation(null);
  };

  const handleFormChange = (field: keyof LessonRequest, value: any) => {
    let newFormData = { ...formData, [field]: value };

    if (field === 'courseId' && isTeacherLocked) {
      const selectedCourse = courses.find(c => c.id === value);
      if (selectedCourse?.teacherId) {
        newFormData.teacherId = selectedCourse.teacherId;
      }
    }

    setFormData(newFormData);
    setTimeout(() => validateLesson(newFormData), 500);
  };

  const validateLesson = async (data: LessonRequest) => {
    try {
      setValidating(true);
      const result = await lessonService.validateLesson(data, editingLesson?.id);
      setValidation(result);
    } catch (err: any) {
      console.error('Validation error:', err);
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const validationResult = await lessonService.validateLesson(formData, editingLesson?.id);

      if (!validationResult.valid) {
        setValidation(validationResult);
        showAlert(t('timetable.conflictDetected'),
          validationResult.conflicts.filter(c => c.blocking).map(c => c.message).join(', '));
        return;
      }

      if (editingLesson) {
        await lessonService.updateLesson(editingLesson.id, formData);
        showAlert(t('common.success'), t('timetable.lessonUpdated'));
      } else {
        await lessonService.createLesson(formData);
        showAlert(t('common.success'), t('timetable.lessonCreated'));
      }

      closeModal();
      loadLessons();
    } catch (err: any) {
      showAlert(t('common.error'), editingLesson ? t('timetable.failedToUpdate') : t('timetable.failedToCreate'));
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lessonId: string) => {
    showConfirm(t('timetable.deleteConfirm'), t('common.areYouSure'), async () => {
      try {
        await lessonService.deleteLesson(lessonId);
        showAlert(t('common.success'), t('timetable.lessonDeleted'));
        loadLessons();
      } catch (err: any) {
        showAlert(t('common.error'), t('timetable.failedToDelete'));
        console.error('Delete error:', err);
      }
    });
  };

  // --- Absence System Handlers ---

  const openAttendanceModal = async (lesson: LessonResponse, date: Date | null) => {
    if (!date) {
      showAlert(t('common.error'), "Error: No date selected for this lesson");
      return;
    }
    setCurrentLessonForAttendance(lesson);
    setCurrentAttendanceDate(date);
    setIsAttendanceModalOpen(true);
    closeModal();

    try {
      setLoading(true);
      const studentsResp = await studentService.searchStudents({
        page: 0,
        size: 1000,
        filters: { classId: lesson.classeId }
      });
      setClassStudents(studentsResp.content);

      const dateStr = date.toISOString().split('T')[0];
      const absences = await absenceService.getAbsencesByLesson(lesson.id, dateStr);
      setAbsentStudentIds(absences.map(a => a.studentId));
    } catch (e) {
      console.error(e);
      showAlert(t('common.error'), t('absences.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const closeAttendanceModal = () => {
    setIsAttendanceModalOpen(false);
    setCurrentLessonForAttendance(null);
    setCurrentAttendanceDate(null);
    setClassStudents([]);
    setAbsentStudentIds([]);
  };

  const toggleStudentAbsence = (studentId: string) => {
    setAbsentStudentIds(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleAttendanceSave = async () => {
    if (!currentLessonForAttendance || !currentAttendanceDate) return;
    try {
      const dateStr = currentAttendanceDate.toISOString().split('T')[0];
      await absenceService.bulkUpdateAbsences({
        lessonId: currentLessonForAttendance.id,
        lessonDate: dateStr,
        studentIds: absentStudentIds
      });
      showAlert(t('common.success'), t('absences.saved'));
      closeAttendanceModal();
    } catch (e) {
      console.error(e);
      showAlert(t('common.error'), t('absences.failedToSave'));
    }
  };

  // -------------------------------

  const formatDuration = (startTime: string, endTime: string): string => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const duration = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    return `${duration} ${t('timetable.minutes')}`;
  };

  const hasBlockingConflicts = validation?.conflicts?.some(c => c.blocking) || false;

  // Custom event content renderer for FullCalendar
  const renderEventContent = (eventInfo: EventContentArg) => {
    const lesson = eventInfo.event.extendedProps as LessonResponse & { durationText?: string };
    const isNextLesson = eventInfo.event.id === nextLessonId;

    return (
      <div className="fc-event-content-custom">
        {isNextLesson && (
          <div className="next-badge">NEXT</div>
        )}
        <div className="event-course-name">{lesson.courseName}</div>
        <div className="event-time">{lesson.startTime} - {lesson.endTime}</div>
        <div className="event-duration">{lesson.durationText}</div>
        {lesson.room && <div className="event-room">📍 {lesson.room}</div>}
      </div>
    );
  };

  if (loading && classes.length === 0) {
    return <div className="timetable-container">{t('common.loading')}</div>;
  }

  return (
    <div className="timetable-container">
      <div className="timetable-header">
        <h1>{t('timetable.title')}</h1>
        <div className="timetable-controls">
          <select
            value={selectedClassId}
            onChange={(e) => setSelectedClassId(e.target.value)}
            className="class-selector"
          >
            <option value="">{t('timetable.selectClass')}</option>
            {classes.map(classe => (
              <option key={classe.id} value={classe.id}>
                {classe.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => openCreateModal()}
            disabled={!selectedClassId}
            className="btn-primary"
          >
            {t('timetable.createLesson')}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {selectedClassId && (
        <div className="calendar-container">
          <FullCalendar
            plugins={[timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            headerToolbar={false}
            locale={calendarLocale}
            direction={calendarDirection}
            events={events}
            height={1000}
            slotMinTime="07:00:00"
            slotMaxTime="23:00:00"
            slotDuration="00:30:00"
            allDaySlot={false}
            selectable={true}
            selectMirror={true}
            select={handleDateSelect}
            eventClick={handleEventClick}
            eventContent={renderEventContent}
            nowIndicator={true}
            weekends={true}
            firstDay={1}
          />
        </div>
      )}

      {/* Lesson Edit/Create Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingLesson ? t('timetable.editLesson') : t('timetable.createLesson')}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>{t('timetable.class')}</label>
                <select
                  value={formData.classeId}
                  onChange={(e) => handleFormChange('classeId', e.target.value)}
                  required
                >
                  <option value="">{t('timetable.selectClass')}</option>
                  {classes.map(classe => (
                    <option key={classe.id} value={classe.id}>
                      {classe.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('timetable.course')}</label>
                <select
                  value={formData.courseId}
                  onChange={(e) => handleFormChange('courseId', e.target.value)}
                  required
                >
                  <option value="">{t('timetable.selectCourse')}</option>
                  {courses
                    .filter(course =>
                      formData.classeId &&
                      course.classIds &&
                      course.classIds.includes(formData.classeId)
                    )
                    .map(course => (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    ))
                  }
                </select>
              </div>

              <div className="form-group">
                <label>
                  {t('timetable.teacher')}
                  {isTeacherLocked && formData.teacherId && formData.courseId && (
                    <span className="teacher-auto-note"> ({t('timetable.autoSelected')})</span>
                  )}
                </label>
                <div className="teacher-input-group">
                  <select
                    value={formData.teacherId}
                    onChange={(e) => handleFormChange('teacherId', e.target.value)}
                    required
                    disabled={isTeacherLocked}
                    className={isTeacherLocked ? 'locked-input' : ''}
                  >
                    <option value="">{t('timetable.selectTeacher')}</option>
                    {teachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.firstName} {teacher.lastName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="teacher-lock-btn"
                    onClick={() => setIsTeacherLocked(!isTeacherLocked)}
                    title={isTeacherLocked ? t('timetable.unlockTeacher') : t('timetable.lockTeacher')}
                  >
                    {isTeacherLocked ? '🔒' : '✏️'}
                  </button>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('timetable.dayOfWeek')}</label>
                  <select
                    value={formData.dayOfWeek}
                    onChange={(e) => handleFormChange('dayOfWeek', e.target.value as DayOfWeek)}
                    required
                  >
                    {DAYS_OF_WEEK.map(day => (
                      <option key={day} value={day}>
                        {t(`timetable.days.${day}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('timetable.room')}</label>
                  <input
                    type="text"
                    value={formData.room}
                    onChange={(e) => handleFormChange('room', e.target.value)}
                    placeholder="e.g., Room 101"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('timetable.startTime')}</label>
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => handleFormChange('startTime', e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('timetable.endTime')}</label>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => handleFormChange('endTime', e.target.value)}
                    required
                  />
                </div>
              </div>

              {formData.startTime && formData.endTime && (
                <div className="duration-info">
                  {t('timetable.duration')}: {formatDuration(formData.startTime, formData.endTime)}
                </div>
              )}

              {/* Validation Results */}
              {validation && validation.conflicts.length > 0 && (
                <div className="validation-results">
                  <h3>{t('timetable.conflicts')}</h3>
                  {validation.conflicts.map((conflict, idx) => (
                    <div
                      key={idx}
                      className={`conflict-item ${conflict.blocking ? 'blocking' : 'warning'}`}
                    >
                      <div className="conflict-type">
                        {conflict.type === 'TEACHER' && '👨‍🏫'}
                        {conflict.type === 'CLASS' && '🎓'}
                        {conflict.type === 'ROOM' && '🚪'}
                        {' '}
                        {t(`timetable.${conflict.type.toLowerCase()}Conflict`)}
                        {conflict.blocking ? ' 🚫' : ' ⚠️'}
                      </div>
                      <div className="conflict-message">{conflict.message}</div>
                      <div className="conflict-info">
                        {conflict.blocking
                          ? t('timetable.blockingConflict')
                          : t('timetable.warningOnly')
                        }
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {validation && validation.valid && (
                <div className="validation-success">
                  ✅ {t('timetable.noConflicts')}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <div className="modal-footer-left">
                {editingLesson && (
                  <button
                    className="btn-info"
                    onClick={() => openAttendanceModal(editingLesson, editingLessonDate)}
                    style={{ backgroundColor: '#2196F3', color: 'white', marginRight: 'auto' }}
                  >
                    📅 {t('absences.markAttendance')} {editingLessonDate ? `(${editingLessonDate.toLocaleDateString()})` : ''}
                  </button>
                )}
              </div>
              <div className="modal-footer-right">
                {editingLesson && (
                  <button
                    className="btn-danger"
                    onClick={() => {
                      handleDelete(editingLesson.id);
                      closeModal();
                    }}
                  >
                    {t('common.delete')}
                  </button>
                )}
                <button className="btn-secondary" onClick={closeModal}>
                  {t('common.cancel')}
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSave}
                  disabled={saving || hasBlockingConflicts}
                >
                  {saving ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Modal */}
      {isAttendanceModalOpen && (
        <div className="modal-overlay" onClick={closeAttendanceModal}>
          <div className="modal-content attendance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('absences.attendance')} - {currentLessonForAttendance?.courseName}</h2>
              <button className="modal-close" onClick={closeAttendanceModal}>×</button>
            </div>

            <div className="modal-body">
              <p className="attendance-subtitle">
                {new Date().toLocaleDateString()} • {currentLessonForAttendance?.startTime} - {currentLessonForAttendance?.endTime}
              </p>

              <div className="students-list">
                {classStudents.length === 0 ? (
                  <p>{t('students.noStudentsFound')}</p>
                ) : (
                  <table className="attendance-table">
                    <thead>
                      <tr>
                        <th>{t('absences.studentName')}</th>
                        <th>{t('absences.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {classStudents.map(student => {
                        const isAbsent = absentStudentIds.includes(student.id!);
                        return (
                          <tr key={student.id} className={isAbsent ? 'absent-row' : ''}>
                            <td>{student.firstName} {student.lastName}</td>
                            <td>
                              <button
                                className={`attendance-toggle ${isAbsent ? 'absent' : 'present'}`}
                                onClick={() => toggleStudentAbsence(student.id!)}
                              >
                                {isAbsent ? t('absences.absent') : t('absences.present')}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeAttendanceModal}>
                {t('common.cancel')}
              </button>
              <button className="btn-primary" onClick={handleAttendanceSave}>
                {t('absences.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timetable;
